/**
 * Модалка «Сравнить с Jira» для одной записи.
 *
 * Открывается из ItemDrawer (JiraSection), показывает per-record diff между
 * текущим состоянием Lens и текущим состоянием Jira issue. Поддерживает оба
 * направления: push (Lens → Jira) и pull (Jira → Lens). По кнопке «Применить»
 * запускает соответствующую mutation.
 */
import { useCallback, useEffect, useState } from 'react';
import { Modal, Tabs, Table, Tag, Typography, Button, Space, Alert, message, Checkbox } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { PushDiffResponse, PullDiffResponse, PushDiffField, PullDiffField } from '@lens/shared';
import { fetchPushDiff, fetchPullDiff, pushItemToJira, bulkJiraPull } from './api.ts';
import { radius } from '../../shared/theme/tokens.ts';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  spaceId: string;
  itemId: string;
  jiraIssueKey: string | null;
  /** Вызывается после успешного push/pull, чтобы parent перезагрузил запись. */
  onApplied?: () => void;
}

type Direction = 'push' | 'pull';

export function JiraCompareModal({
  open, onClose, spaceId, itemId, jiraIssueKey, onApplied,
}: Props): JSX.Element {
  const [direction, setDirection] = useState<Direction>('push');
  const [pushDiff, setPushDiff] = useState<PushDiffResponse | null>(null);
  const [pullDiff, setPullDiff] = useState<PullDiffResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Ключи полей, выбранных для push (slug для обычных, jiraField для fixed). */
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  /** Slugs полей, выбранных для pull (всегда lensFieldSlug). */
  const [pullSelectedKeys, setPullSelectedKeys] = useState<Set<string>>(new Set());

  const load = useCallback(async (dir: Direction) => {
    setLoading(true);
    setError(null);
    try {
      if (dir === 'push') {
        const res = await fetchPushDiff(spaceId, itemId);
        setPushDiff(res);
        setSelectedKeys(new Set(
          res.fields
            .filter((f) => f.willChange)
            .map((f) => f.lensFieldSlug ?? f.jiraField),
        ));
      } else {
        const res = await fetchPullDiff(spaceId, itemId);
        setPullDiff(res);
        setPullSelectedKeys(new Set(
          res.fields.filter((f) => f.willChange).map((f) => f.lensFieldSlug),
        ));
      }
    } catch (err: unknown) {
      const code = typeof err === 'object' && err !== null && 'code' in err
        ? String((err as { code?: unknown }).code) : null;
      const msg = err instanceof Error ? err.message : 'Не удалось получить diff';
      if (code === 'item_not_linked') {
        setError('Запись ещё не связана с Jira — pull недоступен');
      } else if (code === 'no_pull_mapping') {
        setError('Pull-маппинг не настроен для этого типа записей');
      } else if (code === 'no_sync_config') {
        setError('Маппинг Jira не настроен для этого типа записей');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [spaceId, itemId]);

  useEffect(() => {
    if (open) {
      void load(direction);
    }
  }, [open, direction, load]);

  const handleApply = useCallback(async () => {
    setApplying(true);
    try {
      if (direction === 'push') {
        const includeFieldSlugs = selectedKeys.size > 0 ? [...selectedKeys] : undefined;
        const res = await pushItemToJira(spaceId, itemId, { includeFieldSlugs });
        void message.success(res.action === 'created' ? 'Задача создана в Jira' : 'Задача обновлена в Jira');
        onApplied?.();
        onClose();
      } else {
        const includeFieldSlugs = pullSelectedKeys.size > 0 ? [...pullSelectedKeys] : undefined;
        await bulkJiraPull(spaceId, { itemIds: [itemId], ...(includeFieldSlugs ? { includeFieldSlugs } : {}) });
        void message.success('Pull запущен — поля обновятся в фоне');
        onApplied?.();
        onClose();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Ошибка применения';
      void message.error(msg);
    } finally {
      setApplying(false);
    }
  }, [direction, spaceId, itemId, selectedKeys, pullSelectedKeys, onApplied, onClose]);

  const currentDiff = direction === 'push' ? pushDiff : pullDiff;
  const canApply = (currentDiff?.canApply ?? false) && (
    direction === 'push' ? selectedKeys.size > 0 : pullSelectedKeys.size > 0
  );
  const applyLabel = direction === 'push'
    ? (pushDiff?.action === 'create' ? 'Создать в Jira' : 'Обновить в Jira')
    : 'Применить pull';

  const selectedChangedCount = direction === 'push'
    ? (pushDiff?.fields ?? []).filter((f) => f.willChange && selectedKeys.has(f.lensFieldSlug ?? f.jiraField)).length
    : (pullDiff?.fields ?? []).filter((f) => f.willChange && pullSelectedKeys.has(f.lensFieldSlug)).length;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Сравнение с Jira"
      width={900}
      footer={[
        <Button key="reload" icon={<ReloadOutlined />} onClick={() => void load(direction)} disabled={loading || applying}>
          Обновить
        </Button>,
        <Button key="cancel" onClick={onClose} disabled={applying}>Закрыть</Button>,
        <Button
          key="apply"
          type="primary"
          loading={applying}
          disabled={!canApply || loading}
          onClick={() => void handleApply()}
        >
          {applyLabel}
        </Button>,
      ]}
    >
      <Tabs
        activeKey={direction}
        onChange={(k) => setDirection(k as Direction)}
        items={[
          { key: 'push', label: 'Push (Lens → Jira)' },
          { key: 'pull', label: 'Pull (Jira → Lens)', disabled: !jiraIssueKey },
        ]}
      />

      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      )}

      {currentDiff && !error && (
        <>
          <Space size="small" style={{ marginBottom: 12 }}>
            <Tag color={
              currentDiff.action === 'no_change' ? 'default'
                : currentDiff.action === 'create' ? 'blue' : 'gold'
            }>
              {currentDiff.action === 'create' ? 'Создание' : currentDiff.action === 'no_change' ? 'Без изменений' : 'Обновление'}
            </Tag>
            <Text type="secondary">
              {`${selectedChangedCount} из ${currentDiff.changedFieldCount} полей ${direction === 'push' ? 'обновится' : 'будет применено'}`}
            </Text>
            {currentDiff.warnings > 0 && <Tag color="orange">{currentDiff.warnings} предупр.</Tag>}
            {currentDiff.errors > 0 && <Tag color="red">{currentDiff.errors} ошиб.</Tag>}
          </Space>

          {direction === 'push' ? (
            <PushDiffTable
              fields={pushDiff?.fields ?? []}
              selectedKeys={selectedKeys}
              onToggle={(key) => setSelectedKeys((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key); else next.add(key);
                return next;
              })}
            />
          ) : (
            <PullDiffTable
              fields={pullDiff?.fields ?? []}
              selectedKeys={pullSelectedKeys}
              onToggle={(key) => setPullSelectedKeys((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key); else next.add(key);
                return next;
              })}
            />
          )}
        </>
      )}
    </Modal>
  );
}

function PushDiffTable({
  fields,
  selectedKeys,
  onToggle,
}: {
  fields: PushDiffField[];
  selectedKeys: Set<string>;
  onToggle: (key: string) => void;
}): JSX.Element {
  const allKeys = fields.map((f) => f.lensFieldSlug ?? f.jiraField);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedKeys.has(k));
  const someSelected = !allSelected && allKeys.some((k) => selectedKeys.has(k));

  return (
    <Table<PushDiffField>
      dataSource={fields}
      rowKey={(r) => `${r.lensFieldSlug ?? '_fixed'}|${r.jiraField}`}
      size="small"
      pagination={false}
      scroll={{ y: 400 }}
      columns={[
        {
          title: (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => {
                if (allSelected) {
                  allKeys.forEach((k) => selectedKeys.has(k) && onToggle(k));
                } else {
                  allKeys.forEach((k) => !selectedKeys.has(k) && onToggle(k));
                }
              }}
            />
          ),
          width: 40,
          render: (_v, r) => {
            const key = r.lensFieldSlug ?? r.jiraField;
            return <Checkbox checked={selectedKeys.has(key)} onChange={() => onToggle(key)} />;
          },
        },
        {
          title: 'Поле Lens',
          dataIndex: 'lensFieldName',
          render: (_v, r) => r.lensFieldName ?? <Text type="secondary">— фикс. —</Text>,
          width: 180,
        },
        { title: 'Поле Jira', dataIndex: 'jiraField', width: 140, render: (_v, r) => <span title={r.jiraField}>{r.jiraFieldName ?? r.jiraField}</span> },
        {
          title: 'В Jira сейчас',
          dataIndex: 'currentJiraValue',
          render: (v) => <ValueCell value={v} />,
        },
        {
          title: 'Станет в Jira',
          dataIndex: 'newJiraValue',
          render: (v, r) => (
            <span style={r.willChange ? { background: 'var(--color-warning-bg)', padding: '0 4px', borderRadius: radius.sm } : undefined}>
              <ValueCell value={v} />
            </span>
          ),
        },
        {
          title: '',
          dataIndex: 'status',
          width: 80,
          render: (_v, r) => <StatusTag status={r.status} willChange={r.willChange} message={r.message} />,
        },
      ]}
    />
  );
}

function PullDiffTable({
  fields,
  selectedKeys,
  onToggle,
}: {
  fields: PullDiffField[];
  selectedKeys: Set<string>;
  onToggle: (key: string) => void;
}): JSX.Element {
  const allKeys = fields.map((f) => f.lensFieldSlug);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedKeys.has(k));
  const someSelected = !allSelected && allKeys.some((k) => selectedKeys.has(k));

  return (
    <Table<PullDiffField>
      dataSource={fields}
      rowKey={(r) => `${r.jiraField}|${r.lensFieldSlug}`}
      size="small"
      pagination={false}
      scroll={{ y: 400 }}
      columns={[
        {
          title: (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => {
                if (allSelected) {
                  allKeys.forEach((k) => selectedKeys.has(k) && onToggle(k));
                } else {
                  allKeys.forEach((k) => !selectedKeys.has(k) && onToggle(k));
                }
              }}
            />
          ),
          width: 40,
          render: (_v, r) => (
            <Checkbox checked={selectedKeys.has(r.lensFieldSlug)} onChange={() => onToggle(r.lensFieldSlug)} />
          ),
        },
        { title: 'Поле Jira', dataIndex: 'jiraField', width: 140, render: (_v, r) => <span title={r.jiraField}>{r.jiraFieldName ?? r.jiraField}</span> },
        { title: 'Поле Lens', dataIndex: 'lensFieldName', width: 180, render: (v, r) => v ?? r.lensFieldSlug },
        {
          title: 'В Lens сейчас',
          dataIndex: 'currentLensValue',
          render: (v) => <ValueCell value={v} />,
        },
        {
          title: 'Станет в Lens',
          dataIndex: 'newLensValue',
          render: (v, r) => (
            <span style={r.willChange ? { background: 'var(--color-warning-bg)', padding: '0 4px', borderRadius: radius.sm } : undefined}>
              <ValueCell value={v} />
            </span>
          ),
        },
        {
          title: '',
          dataIndex: 'status',
          width: 80,
          render: (_v, r) => <StatusTag status={r.status} willChange={r.willChange} message={r.message} />,
        },
      ]}
    />
  );
}

function ValueCell({ value }: { value: unknown }): JSX.Element {
  if (value === null || value === undefined) return <Text type="secondary">—</Text>;
  if (typeof value === 'string') return <span>{value || <Text type="secondary">—</Text>}</span>;
  if (typeof value === 'number' || typeof value === 'boolean') return <span>{String(value)}</span>;
  if (value instanceof Date) return <span>{value.toLocaleString('ru-RU')}</span>;
  return <Text code style={{ fontSize: 11 }}>{JSON.stringify(value)}</Text>;
}

function StatusTag({ status, willChange, message: msg }: { status: 'ok'|'warning'|'error'; willChange: boolean; message: string | null }): JSX.Element {
  if (status === 'error') return <Tag color="red" title={msg ?? ''}>err</Tag>;
  if (status === 'warning') return <Tag color="orange" title={msg ?? ''}>warn</Tag>;
  if (willChange) return <Tag color="gold">изм.</Tag>;
  return <Tag>=</Tag>;
}
